import AppsOutlined from '@mui/icons-material/AppsOutlined';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import PublicOutlined from '@mui/icons-material/PublicOutlined';
import ViewQuiltOutlined from '@mui/icons-material/ViewQuiltOutlined';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ComponentType, ReactNode } from 'react';
import { useState } from 'react';
import { ErrorState } from '../../../media/shared/ErrorState.js';
import { MediaCard } from '../../../media/shared/MediaCard.js';
import {
  type MediaColumn,
  MediaTable,
  type MediaTableRow,
} from '../../../media/shared/MediaTable.js';
import { NoData } from '../../../media/shared/NoData.js';
import { formatMetricValue } from '../../data/format.js';
import { MetricDelta } from '../../MetricDelta.js';
import { NetworkGlyph } from '../../NetworkGlyph.js';
import { ELLIPSIS_SX, TOGGLE_BUTTON_SX, TOOLBAR_ROW_SX } from '../../utils.js';
import {
  type BreakdownDimension,
  type CreativeDetailScope,
  useSpendBreakdown,
} from '../data/api.js';
import { SECTION_TITLE_SX } from '../shared/SectionHeader.js';
import { SectionSkeleton } from '../shared/SectionSkeleton.js';
import { type Comparison, ComparisonPicker } from './ComparisonPicker.js';

const DIMENSIONS: {
  value: BreakdownDimension;
  label: string;
  Icon: ComponentType<SvgIconProps>;
}[] = [
  { value: 'country', label: 'Country', Icon: PublicOutlined },
  { value: 'placement', label: 'Placement', Icon: ViewQuiltOutlined },
  { value: 'platform', label: 'Platform', Icon: AppsOutlined },
];

const COUNTRY_CODE_PATTERN = /^[A-Za-z]{2}$/;
const TABLE_MAX_HEIGHT = 420;

interface SpendBreakdownSectionProps {
  scope: CreativeDetailScope;
  creativeId: string;
  kpis: string[];
}

function CodeChip({ code }: { code: string }): ReactNode {
  return (
    <Box
      component="span"
      sx={(theme) => ({
        flexShrink: 0,
        minWidth: 24,
        height: 18,
        px: 0.5,
        display: 'grid',
        placeItems: 'center',
        borderRadius: `${theme.radius.sm}px`,
        bgcolor: 'background.platform',
        color: 'text.secondary',
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '.04em',
      })}
    >
      {code.slice(0, 2).toUpperCase()}
    </Box>
  );
}

export function SpendBreakdownSection({
  scope,
  creativeId,
  kpis,
}: SpendBreakdownSectionProps): ReactNode {
  const [dimension, setDimension] = useState<BreakdownDimension>('country');
  const [comparison, setComparison] = useState<Comparison>('previous');
  const [zoomOpen, setZoomOpen] = useState(false);
  const breakdown = useSpendBreakdown({ ...scope, creativeId, kpis, dimension }, true);

  const toolbar = (
    <Box
      sx={{
        ...TOOLBAR_ROW_SX,
        mb: 0,
        py: 2,
        px: 4,
        gap: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={dimension}
        onChange={(_event, value: BreakdownDimension | null) => {
          if (value !== null) setDimension(value);
        }}
        sx={{ backgroundColor: 'background.page' }}
      >
        {DIMENSIONS.map((entry) => (
          <ToggleButton
            key={entry.value}
            value={entry.value}
            sx={{ ...TOGGLE_BUTTON_SX, fontSize: 11, py: 0.5, px: 1.5 }}
          >
            <entry.Icon sx={{ fontSize: 14, mr: 1 }} />
            {entry.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <ComparisonPicker
          value={comparison}
          onChange={setComparison}
          {...(breakdown.data === undefined
            ? {}
            : { previousDateRange: breakdown.data.previousDateRange })}
        />
        <IconButton
          size="small"
          aria-label="Open full size"
          onClick={() => setZoomOpen(true)}
          sx={(theme) => ({
            width: 28,
            height: 28,
            border: '1px solid',
            borderColor: theme.palette.border.strong,
            color: 'text.secondary',
            borderRadius: `${theme.radius.md}px`,
          })}
        >
          <OpenInNewOutlinedIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>
    </Box>
  );

  if (breakdown.isError) {
    return (
      <MediaCard sx={{ overflow: 'hidden' }}>
        {toolbar}
        <ErrorState />
      </MediaCard>
    );
  }

  if (breakdown.data === undefined) {
    return (
      <MediaCard sx={{ overflow: 'hidden' }}>
        {toolbar}
        <SectionSkeleton variant="table" rowCount={4} />
      </MediaCard>
    );
  }

  const columns: MediaColumn[] = [
    { key: 'dimension', label: dimension, width: 'minmax(150px, 1fr)' },
    ...breakdown.data.columns.map((column) => ({
      key: column.name,
      label: column.label,
      width: '100px',
      align: 'right' as const,
    })),
  ];

  const rows: MediaTableRow[] = breakdown.data.rows.map((row) => ({
    id: row.value,
    cells: {
      dimension: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          {dimension === 'country' && COUNTRY_CODE_PATTERN.test(row.value) && (
            <CodeChip code={row.value} />
          )}
          {dimension === 'platform' && <NetworkGlyph network={row.value} />}
          {dimension === 'placement' && <CodeChip code={row.label} />}
          <Tooltip title={row.label} placement="top">
            <Typography
              component="span"
              sx={{ fontSize: 13, fontWeight: 500, minWidth: 0, ...ELLIPSIS_SX }}
            >
              {row.label}
            </Typography>
          </Tooltip>
          <Typography
            component="span"
            sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }}
          >
            {row.spendShare === null ? '—' : `${Math.round(row.spendShare * 100)}%`}
          </Typography>
        </Box>
      ),
      ...Object.fromEntries(
        breakdown.data.columns.map((column) => {
          const metric = row.metrics[column.name];
          const comparisonValue =
            comparison === 'off' || metric === undefined ? null : metric[comparison];
          return [
            column.name,
            <Box
              key={column.name}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                {metric === undefined
                  ? '—'
                  : formatMetricValue(metric.value, metric.format, breakdown.data.currencySymbol)}
              </Typography>
              {comparison !== 'off' &&
                (comparisonValue === null ? (
                  <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>—</Typography>
                ) : (
                  <MetricDelta
                    delta={comparisonValue.delta}
                    sentiment={comparisonValue.sentiment}
                  />
                ))}
            </Box>,
          ];
        }),
      ),
    },
  }));

  const tableBody =
    rows.length === 0 ? (
      <NoData message="No spend recorded for this breakdown." />
    ) : (
      <Box sx={{ overflowX: 'auto', overflowY: 'visible' }}>
        <MediaTable columns={columns} rows={rows} />
      </Box>
    );

  return (
    <>
      <MediaCard sx={{ overflow: 'hidden' }}>
        {toolbar}
        <Box sx={{ maxHeight: TABLE_MAX_HEIGHT, overflowX: 'hidden', overflowY: 'auto' }}>
          {tableBody}
        </Box>
      </MediaCard>
      <Dialog
        open={zoomOpen}
        onClose={() => setZoomOpen(false)}
        maxWidth={false}
        slotProps={{ paper: { sx: { width: 920, maxWidth: '90vw' } } }}
      >
        <DialogContent sx={{ p: 4 }}>
          <IconButton
            size="small"
            onClick={() => setZoomOpen(false)}
            aria-label="Close"
            sx={{ position: 'absolute', top: 12, right: 12, color: 'text.secondary' }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography sx={{ ...SECTION_TITLE_SX, mb: 3 }}>Where the spend went</Typography>
          {tableBody}
        </DialogContent>
      </Dialog>
    </>
  );
}
